import { formatRecordingTime } from './voice-recorder';

const SEEK_STEP_SECONDS = 5;

/**
 * audioPlayer — waveform player for voice note bubbles.
 *
 * Peaks are captured while recording and travel with the message, so the
 * waveform draws from the message payload and no audio is fetched until the
 * listener actually presses play.
 */
export function audioPlayer(url, peaks, duration, { playbackFailed } = {}) {
  return {
    url,
    peaks: peaks || [],
    duration: duration || 0,
    isPlaying: false,
    currentTime: 0,
    hasError: false,
    playbackFailed,

    get progress() {
      if (!this.duration) return 0;
      return Math.min(1, this.currentTime / this.duration);
    },

    get displayTime() {
      return formatRecordingTime(this.isPlaying ? this.currentTime : this.duration);
    },

    barPlayed(index) {
      return index / this.peaks.length < this.progress;
    },

    barHeight(peak) {
      return Math.max(3, Math.round((peak / 100) * 28));
    },

    togglePlay() {
      const audio = this.$refs.audio;
      if (!audio) return;

      // Reload after a failed fetch so a transient network or decode error
      // can be retried from the same button.
      if (this.hasError) {
        this.hasError = false;
        this._playWhenReady = true;
        audio.load();
        return;
      }

      if (audio.paused) {
        audio.play().catch(() => this.markFailed());
      } else {
        audio.pause();
      }
    },

    seekToFraction(fraction) {
      const audio = this.$refs.audio;
      if (!audio) return;

      const target = Math.min(this.duration, Math.max(0, fraction * this.duration));
      this.currentTime = target;

      if (this.hasError) {
        this.hasError = false;
        this._pendingSeek = target;
        audio.load();
        return;
      }

      // With preload off there is no media loaded to seek until playback has
      // started, so the position is held and applied once metadata arrives.
      if (audio.readyState === 0) {
        this._pendingSeek = target;
        audio.load();
      } else {
        audio.currentTime = target;
      }
    },

    seekToPointer(event) {
      const { left, width } = event.currentTarget.getBoundingClientRect();
      this.seekToFraction((event.clientX - left) / width);
    },

    seekBy(seconds) {
      this.seekToFraction((this.currentTime + seconds) / this.duration);
    },

    applyPendingSeek() {
      if (this._pendingSeek !== undefined) {
        this.$refs.audio.currentTime = this._pendingSeek;
        this._pendingSeek = undefined;
      }
      if (!this._playWhenReady) return;

      this._playWhenReady = false;
      this.$refs.audio.play().catch(() => this.markFailed());
    },

    handleEnded() {
      this.isPlaying = false;
      this.currentTime = 0;
    },

    markFailed() {
      if (this.hasError) return;
      this.hasError = true;
      this.isPlaying = false;
      this._playWhenReady = false;
      this.$dispatch('chat:voice-playback-failed', { message: this.playbackFailed });
    },

    get seekLabelSeconds() {
      return Math.round(this.currentTime);
    },

    handleSeekKey(event) {
      const step = { ArrowRight: SEEK_STEP_SECONDS, ArrowLeft: -SEEK_STEP_SECONDS }[event.key];
      if (step === undefined) return;

      event.preventDefault();
      this.seekBy(step);
    },
  };
}
